import {Card, Form, FormButton, FormField} from 'platform/components';
import {HStack, Text, VStack} from 'platform/foundation';
import {z} from 'zod';

import {useState} from 'react';

import {ApiError} from '@dms/api/shared';
import {
  usePostPowerOfAttorneyMutation,
  usePostServiceOrderOverviewMutation,
  usePostTireStorageFormMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {workshopRoutes} from '@dms/routes';
import {handleApiError, ORDER_TABS, queryParams} from '@dms/shared';

import {composePath, RequiredTestIdProps, suffixTestId, useNavigate} from 'shared/web';

/**
 * Form schema for additional documents selection
 */
const additionalDocumentsSchema = z.object({
  tireStorageForm: z.boolean(),
  powerOfAttorney: z.boolean(),
  orderDetails: z.boolean(),
});

type AdditionalDocumentsFormData = z.infer<typeof additionalDocumentsSchema>;

export interface AdditionalDocumentsDialogProps extends RequiredTestIdProps {
  serviceCaseId: string;
  serviceOrderId: string;
  onClose: () => void;
  refreshDocuments: () => void;
}

/**
 * Dialog component for selecting and generating additional documents
 *
 * @param props - Component props including service case ID, order ID, and callbacks
 */
export function AdditionalDocumentsDialog(props: AdditionalDocumentsDialogProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const navigate = useNavigate();

  const [postTireStorageForm, {isLoading: isPostTireStorageFormLoading}] =
    usePostTireStorageFormMutation();
  const [postPowerOfAttorney, {isLoading: isPostPowerOfAttorneyLoading}] =
    usePostPowerOfAttorneyMutation();
  const [postServiceOrderOverview, {isLoading: isPostServiceOrderOverviewLoading}] =
    usePostServiceOrderOverviewMutation();

  const defaultValues: AdditionalDocumentsFormData = {
    tireStorageForm: true,
    powerOfAttorney: true,
    orderDetails: true,
  };

  const handleGenerate = async (values: AdditionalDocumentsFormData) => {
    const selectedDocuments = Object.entries(values)
      .filter(([, isSelected]) => isSelected)
      .map(([key]) => key);

    if (selectedDocuments.length === 0) {
      return;
    }

    setIsGenerating(true);

    try {
      const documentPromises = [];

      if (values.tireStorageForm) {
        documentPromises.push(
          postTireStorageForm({
            serviceCaseId: props.serviceCaseId,
            serviceOrderId: props.serviceOrderId,
          }).unwrap()
        );
      }

      if (values.powerOfAttorney) {
        documentPromises.push(
          postPowerOfAttorney({
            serviceCaseId: props.serviceCaseId,
            serviceOrderId: props.serviceOrderId,
          }).unwrap()
        );
      }

      if (values.orderDetails) {
        documentPromises.push(
          postServiceOrderOverview({
            serviceCaseId: props.serviceCaseId,
            serviceOrderId: props.serviceOrderId,
          }).unwrap()
        );
      }

      await Promise.all(documentPromises);

      navigate(
        composePath(workshopRoutes.serviceCaseDetail, {
          params: {
            id: props.serviceCaseId,
          },
          queryParams: {
            [queryParams.SERVICE_CASE_ORDER_ID]: props.serviceOrderId,
            [queryParams.SERVICE_CASE_ORDER_TAB]: ORDER_TABS.DOCUMENTS,
          },
          isSearchParamsPreserved: false,
        })
      );

      props.refreshDocuments();
      props.onClose();
    } catch (error) {
      handleApiError(error as ApiError);
    } finally {
      setIsGenerating(false);
    }
  };

  const isDocumentGenerating =
    isPostTireStorageFormLoading ||
    isPostPowerOfAttorneyLoading ||
    isPostServiceOrderOverviewLoading;

  return (
    <Form
      experimentalZodSchema={additionalDocumentsSchema}
      defaultValues={defaultValues}
      onSubmit={handleGenerate}
    >
      {(control, formApi) => (
        <VStack spacing={6}>
          <VStack spacing={4}>
            <Card variant="inlineGrey">
              <HStack spacing={3} align="center">
                <FormField name="tireStorageForm" type="checkbox" control={control} />
                <Text alternative>{i18n.t('entity.order.documents.tire-storage-form')}</Text>
              </HStack>
            </Card>
            <Card variant="inlineGrey">
              <HStack spacing={3} align="center">
                <FormField name="powerOfAttorney" type="checkbox" control={control} />
                <Text alternative>{i18n.t('entity.order.documents.power-of-attorney')}</Text>
              </HStack>
            </Card>
            <Card variant="inlineGrey">
              <HStack spacing={3} align="center">
                <FormField name="orderDetails" type="checkbox" control={control} />
                <Text alternative>{i18n.t('entity.order.documents.order-details')}</Text>
              </HStack>
            </Card>
          </VStack>

          <HStack spacing={2} justify="flex-end">
            <FormButton
              control={control}
              title={i18n.t('general.actions.cancel')}
              variant="secondary"
              onClick={props.onClose}
              data-testid={suffixTestId('cancel', props)}
            />
            <FormButton
              control={control}
              title={i18n.t('entity.order.actions.generate')}
              type="submit"
              isDisabled={
                isDocumentGenerating ||
                (!formApi.watch('tireStorageForm') &&
                  !formApi.watch('powerOfAttorney') &&
                  !formApi.watch('orderDetails'))
              }
              isLoading={isGenerating || isDocumentGenerating}
              data-testid={suffixTestId('generate', props)}
            />
          </HStack>
        </VStack>
      )}
    </Form>
  );
}
